import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Leaf, Sparkles, Droplet, HeartHandshake, ChevronDown, ShieldCheck, Download } from "lucide-react";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useLanguage } from "@/i18n/LanguageContext";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import Seo from "@/components/Seo";

/* -------------------------------------------------------------------------- */
/* DATA. Trilingual (FR / EN / PT), aligned with NANDA SÁ premium identity    */
/* -------------------------------------------------------------------------- */

const FAMILLES = [
  {
    key: "chantillys",
    anchor: "#collection",
    label: { fr: "Chantilly & soins fouettés", en: "Whipped butters & care", pt: "Chantillys & cuidados batidos" },
    desc: {
      fr: "Quatre soins fouettés autour du beurre de karité. Disponibles.",
      en: "Four whipped cares around shea butter. Available.",
      pt: "Quatro cuidados batidos em torno da manteiga de karité. Disponíveis.",
    },
  },
  {
    key: "huiles",
    anchor: "#huiles",
    label: { fr: "Huiles cosmétiques", en: "Cosmetic oils", pt: "Óleos cosméticos" },
    desc: {
      fr: "Huiles végétales de la Maison, pressées à froid. Formats sur demande.",
      en: "The House's plant oils, cold-pressed. Formats on request.",
      pt: "Óleos vegetais da Casa, prensados a frio. Formatos a pedido.",
    },
  },
  {
    key: "savons",
    anchor: "#savons",
    label: { fr: "Savons & soins", en: "Soaps & care", pt: "Sabonetes & cuidados" },
    desc: {
      fr: "Savon noir artisanal, fabriqué selon la tradition ouest-africaine. Disponible.",
      en: "Artisanal black soap, made following West African tradition. Available.",
      pt: "Sabonete preto artesanal, fabricado segundo a tradição da África Ocidental. Disponível.",
    },
  },
  {
    key: "baumes",
    anchor: "#baumes",
    label: { fr: "Baumes", en: "Balms", pt: "Bálsamos" },
    desc: {
      fr: "Formulations en cours de développement.",
      en: "Formulas currently in development.",
      pt: "Formulações em desenvolvimento.",
    },
  },
];

/* ------------------------------------------------------------------ */
/* CHANTILLYS NANDA SÁ, photos réelles des produits, telles que      */
/* fournies par la Maison. Aucun prix, aucune allégation médicale.    */
/* ------------------------------------------------------------------ */
const CHANTILLYS = [
  {
    slug: "chantilly-karite-avocat-amande",
    image: "/photos/nanda-cosmetiques/chantilly-karite-avocat-amande.jpg",
    textureNote: null,
    name: { fr: "Chantilly Karité – Avocat – Amande douce", en: "Shea – Avocado – Sweet Almond Whipped Butter", pt: "Chantilly Karité – Abacate – Amêndoa doce" },
    type: { fr: "Soin fouetté · Corps & cheveux", en: "Whipped care · Body & hair", pt: "Cuidado batido · Corpo & cabelo" },
    volume: "100 ml",
    ingredients: { fr: "Beurre de karité, huile d'avocat, huile d'amande douce", en: "Shea butter, avocado oil, sweet almond oil", pt: "Manteiga de karité, óleo de abacate, óleo de amêndoa doce" },
    usage: { fr: "Prélever une noisette, faire fondre entre les paumes, appliquer en massage doux. Usage externe.", en: "Take a small amount, melt between the palms, apply with a gentle massage. External use.", pt: "Retirar uma pequena porção, derreter entre as palmas, aplicar com massagem suave. Uso externo." },
  },
  {
    slug: "chantilly-baobab-karite",
    image: "/photos/nanda-cosmetiques/chantilly-baobab-karite.jpg",
    textureNote: null,
    name: { fr: "Chantilly Baobab Karité", en: "Baobab & Shea Whipped Butter", pt: "Chantilly Baobá Karité" },
    type: { fr: "Soin fouetté · Corps & cheveux", en: "Whipped care · Body & hair", pt: "Cuidado batido · Corpo & cabelo" },
    volume: "100 ml",
    ingredients: { fr: "Beurre de karité, huile de baobab", en: "Shea butter, baobab oil", pt: "Manteiga de karité, óleo de baobá" },
    usage: { fr: "Prélever une noisette, faire fondre entre les paumes, appliquer en massage doux. Usage externe.", en: "Take a small amount, melt between the palms, apply with a gentle massage. External use.", pt: "Retirar uma pequena porção, derreter entre as palmas, aplicar com massagem suave. Uso externo." },
  },
  {
    slug: "chantilly-karite-hibiscus",
    image: "/photos/nanda-cosmetiques/chantilly-karite-hibiscus.jpg",
    textureNote: null,
    name: { fr: "Chantilly Karité Hibiscus", en: "Shea & Hibiscus Whipped Butter", pt: "Chantilly Karité Hibisco" },
    type: { fr: "Soin fouetté · Corps & cheveux", en: "Whipped care · Body & hair", pt: "Cuidado batido · Corpo & cabelo" },
    volume: "100 ml",
    ingredients: { fr: "Beurre de karité, hibiscus", en: "Shea butter, hibiscus", pt: "Manteiga de karité, hibisco" },
    usage: { fr: "Prélever une noisette, faire fondre entre les paumes, appliquer en massage doux. Usage externe.", en: "Take a small amount, melt between the palms, apply with a gentle massage. External use.", pt: "Retirar uma pequena porção, derreter entre as palmas, aplicar com massagem suave. Uso externo." },
  },
  {
    slug: "karite-fondant",
    image: "/photos/nanda-cosmetiques/karite-fondant.png",
    textureNote: null,
    name: { fr: "Karité Fondant", en: "Melting Shea Butter", pt: "Karité Fondant" },
    type: { fr: "Beurre corporel · Corps & cheveux", en: "Body butter · Body & hair", pt: "Manteiga corporal · Corpo & cabelo" },
    volume: "250 g",
    ingredients: { fr: "Beurre de karité", en: "Shea butter", pt: "Manteiga de karité" },
    usage: { fr: "Faire fondre une noisette entre les paumes avant application. Usage externe.", en: "Melt a small amount between the palms before applying. External use.", pt: "Derreter uma pequena porção entre as palmas antes de aplicar. Uso externo." },
  },
];

const SAVONS = [
  {
    slug: "savon-noir",
    image: "/photos/nanda-cosmetiques/savon-noir.jpg",
    name: { fr: "Savon Noir", en: "Black Soap", pt: "Sabonete Preto" },
    type: { fr: "Nettoyant traditionnel · Corps", en: "Traditional cleanser · Body", pt: "Limpeza tradicional · Corpo" },
    volume: null,
    ingredients: { fr: "Fabriqué selon la tradition ouest-africaine. Composition détaillée sur demande.", en: "Made following West African tradition. Detailed composition on request.", pt: "Fabricado segundo a tradição da África Ocidental. Composição detalhada a pedido." },
    usage: { fr: "Corps. Usage externe.", en: "Body. External use.", pt: "Corpo. Uso externo." },
  },
];

const VALUES = [
  { icon: Leaf, key: "natural", fr: "Naturel", en: "Natural", pt: "Natural" },
  { icon: Sparkles, key: "simple", fr: "Simple", en: "Simple", pt: "Simples" },
  { icon: HeartHandshake, key: "local", fr: "Local", en: "Local", pt: "Local" },
  { icon: ShieldCheck, key: "artisanal", fr: "Artisanal", en: "Artisanal", pt: "Artesanal" },
  { icon: Droplet, key: "responsible", fr: "Responsable", en: "Responsible", pt: "Responsável" },
];

const INGREDIENTS = [
  {
    name: "Karité",
    latin: "Vitellaria paradoxa",
    fr: "Beurre végétal au cœur de toutes nos chantillys et de notre Karité Fondant.",
    en: "The plant butter at the heart of all our whipped butters and our Karité Fondant.",
    pt: "A manteiga vegetal no centro de todas as nossas chantillys e do nosso Karité Fondant.",
  },
  {
    name: "Baobab",
    latin: "Adansonia digitata",
    fr: "Huile végétale de notre Chantilly Baobab Karité.",
    en: "The plant oil in our Baobab & Shea Whipped Butter.",
    pt: "O óleo vegetal da nossa Chantilly Baobá Karité.",
  },
  {
    name: "Hibiscus",
    latin: "Hibiscus sabdariffa",
    fr: "Présent dans notre Chantilly Karité Hibiscus.",
    en: "Present in our Shea & Hibiscus Whipped Butter.",
    pt: "Presente na nossa Chantilly Karité Hibisco.",
  },
  {
    name: "Avocat",
    latin: "Persea gratissima",
    fr: "Huile végétale de notre Chantilly Karité – Avocat – Amande douce.",
    en: "The plant oil in our Shea – Avocado – Sweet Almond Whipped Butter.",
    pt: "O óleo vegetal da nossa Chantilly Karité – Abacate – Amêndoa doce.",
  },
  {
    name: "Amande douce",
    latin: "Prunus amygdalus dulcis",
    fr: "Huile végétale de notre Chantilly Karité – Avocat – Amande douce.",
    en: "The plant oil in our Shea – Avocado – Sweet Almond Whipped Butter.",
    pt: "O óleo vegetal da nossa Chantilly Karité – Abacate – Amêndoa doce.",
  },
];

const RITUAL = [
  {
    step: "01",
    fr: "Sourcing local",
    en: "Local sourcing",
    pt: "Sourcing local",
    descFr: "Ingrédients récoltés en Afrique de l'Ouest, auprès de coopératives partenaires.",
    descEn: "Ingredients harvested in West Africa, from partner cooperatives.",
    descPt: "Ingredientes colhidos na África Ocidental, junto de cooperativas parceiras.",
  },
  {
    step: "02",
    fr: "Transformation",
    en: "Processing",
    pt: "Transformação",
    descFr: "Extraction à froid, filtrations douces, respect des savoir-faire traditionnels.",
    descEn: "Cold extraction, gentle filtration, respect for traditional know-how.",
    descPt: "Extracção a frio, filtrações suaves, respeito pelos saberes tradicionais.",
  },
  {
    step: "03",
    fr: "Formulation",
    en: "Formulation",
    pt: "Formulação",
    descFr: "Formules courtes et textures agréables, ajustées avec soin.",
    descEn: "Short formulas and pleasant textures, carefully adjusted.",
    descPt: "Fórmulas curtas e texturas agradáveis, ajustadas com cuidado.",
  },
  {
    step: "04",
    fr: "Conditionnement",
    en: "Packaging",
    pt: "Embalagem",
    descFr: "Pots réutilisables et étiquettes trilingues, pensés pour durer.",
    descEn: "Reusable jars and trilingual labels, designed to last.",
    descPt: "Frascos reutilizáveis e etiquetas trilingues, pensados para durar.",
  },
];

const FAQ = [
  {
    q: { fr: "Vos cosmétiques sont-ils 100 % naturels ?", en: "Are your cosmetics 100% natural?", pt: "Os cosméticos são 100% naturais?" },
    a: {
      fr: "Nos soins sont formulés autour de beurres et d'huiles végétales d'Afrique de l'Ouest, avec des listes d'ingrédients courtes. Le détail de chaque formule est indiqué sur sa fiche.",
      en: "Our care products are formulated around West African plant butters and oils, with short ingredient lists. The details of each formula are shown on its card.",
      pt: "Os nossos cuidados são formulados em torno de manteigas e óleos vegetais da África Ocidental, com listas de ingredientes curtas. O detalhe de cada fórmula está indicado na sua ficha.",
    },
  },
  {
    q: { fr: "Où sont fabriqués les produits NANDA SÁ ?", en: "Where are NANDA SÁ products made?", pt: "Onde são fabricados os produtos NANDA SÁ?" },
    a: {
      fr: "Nos soins sont fabriqués par nos équipes. NANDA SÁ est présente au Sénégal et en Guinée-Bissau, et la transformation locale est au cœur de notre démarche.",
      en: "Our care products are made by our teams. NANDA SÁ is present in Senegal and Guinea-Bissau, and local processing is at the heart of our approach.",
      pt: "Os nossos cuidados são fabricados pelas nossas equipas. A NANDA SÁ está presente no Senegal e na Guiné-Bissau, e a transformação local está no centro da nossa abordagem.",
    },
  },
  {
    q: { fr: "Quels ingrédients privilégiez-vous ?", en: "Which ingredients do you prioritise?", pt: "Que ingredientes privilegia?" },
    a: {
      fr: "Nos soins disponibles utilisent le beurre de karité, l'huile de baobab, l'hibiscus, l'huile d'avocat et l'huile d'amande douce. Cette liste évolue avec nos lancements.",
      en: "Our available care products use shea butter, baobab oil, hibiscus, avocado oil and sweet almond oil. This list evolves with our launches.",
      pt: "Os nossos cuidados disponíveis utilizam a manteiga de karité, o óleo de baobá, o hibisco, o óleo de abacate e o óleo de amêndoa doce. Esta lista evolui com os nossos lançamentos.",
    },
  },
  {
    q: { fr: "Proposez-vous une distribution B2B ?", en: "Do you offer B2B distribution?", pt: "Oferecem distribuição B2B?" },
    a: {
      fr: "Oui. Hôtels, spas, revendeurs et distributeurs peuvent nous contacter via le formulaire Devis. Nous proposons des conditions dédiées et du co-branding sur demande.",
      en: "Yes. Hotels, spas, resellers and distributors can contact us via the Quote form. We offer dedicated conditions and co-branding on request.",
      pt: "Sim. Hotéis, spas, revendedores e distribuidores podem contactar-nos via o formulário de Orçamento. Oferecemos condições dedicadas e co-branding a pedido.",
    },
  },
  {
    q: { fr: "Les emballages sont-ils durables ?", en: "Is your packaging sustainable?", pt: "As embalagens são sustentáveis?" },
    a: {
      fr: "Nos soins sont aujourd'hui conditionnés dans des pots réutilisables. Nous travaillons à améliorer progressivement nos emballages.",
      en: "Our care products are currently packaged in reusable jars. We are working on steadily improving our packaging.",
      pt: "Os nossos cuidados são hoje acondicionados em frascos reutilizáveis. Estamos a trabalhar para melhorar progressivamente as nossas embalagens.",
    },
  },
  {
    q: { fr: "Puis-je acheter en ligne ?", en: "Can I buy online?", pt: "Posso comprar online?" },
    a: {
      fr: "Notre boutique en ligne officielle sera lancée prochainement. En attendant, les commandes B2B, hôtels et revendeurs se font via le formulaire de contact.",
      en: "Our official online store will launch soon. In the meantime, B2B, hotel and reseller orders are placed via the contact form.",
      pt: "A nossa loja oficial online será lançada em breve. Entretanto, encomendas B2B, hotéis e revendedores fazem-se via o formulário de contacto.",
    },
  },
];

/* -------------------------------------------------------------------------- */
/* Helpers */
/* -------------------------------------------------------------------------- */

const pick = (obj, locale) => obj[locale] || obj.fr;

const Section = ({ children, className = "", id }) => (
  <section id={id} className={`border-t border-nanda-line ${className}`}>
    <div className="mx-auto max-w-7xl px-6 sm:px-12 py-24 md:py-32">{children}</div>
  </section>
);

const Overline = ({ children, tone = "text-nanda-muted" }) => (
  <div className={`label-overline ${tone} mb-6`}>{children}</div>
);

/* -------------------------------------------------------------------------- */
/* Page */
/* -------------------------------------------------------------------------- */

export default function NandaCosmetiques() {
  const { locale } = useLanguage();
  const [openFaq, setOpenFaq] = React.useState(null);

  const t = {
    heroH1: {
      fr: "Cosmétiques Naturels",
      en: "Natural Cosmetics",
      pt: "Cosméticos Naturais",
    }[locale] || "Cosmétiques Naturels",
    heroBy: { fr: "par NANDA SÁ", en: "by NANDA SÁ", pt: "por NANDA SÁ" }[locale],
    heroSub: {
      fr: "Des soins simples, formulés autour des beurres et huiles végétales d'Afrique de l'Ouest : karité, baobab, hibiscus, avocat, amande douce. Fabriqués par NANDA SÁ, présente au Sénégal et en Guinée-Bissau.",
      en: "Simple care products, formulated around West African plant butters and oils: shea, baobab, hibiscus, avocado, sweet almond. Made by NANDA SÁ, present in Senegal and Guinea-Bissau.",
      pt: "Cuidados simples, formulados em torno das manteigas e óleos vegetais da África Ocidental: karité, baobá, hibisco, abacate, amêndoa doce. Fabricados pela NANDA SÁ, presente no Senegal e na Guiné-Bissau.",
    }[locale],
    ctaDiscover: { fr: "Découvrir la collection", en: "Discover the collection", pt: "Descobrir a colecção" }[locale],
    ctaQuote: { fr: "Demander un devis B2B", en: "Request a B2B quote", pt: "Pedir um orçamento B2B" }[locale],
    categoriesSubtitle: {
      fr: "Quatre familles de soins, développées à notre rythme. Ce qui est présenté ici est disponible, ou clairement annoncé comme en développement.",
      en: "Four families of care, developed at our own pace. What is shown here is available, or clearly announced as in development.",
      pt: "Quatro famílias de cuidados, desenvolvidas ao nosso ritmo. O que é apresentado aqui está disponível, ou claramente anunciado como em desenvolvimento.",
    }[locale],
    productsTitle: { fr: "Chantilly & soins fouettés", en: "Whipped butters & care", pt: "Chantillys & cuidados batidos" }[locale],
    productsSubtitle: {
      fr: "Quatre soins autour du beurre de karité. Les photos sont celles des produits, tels quels.",
      en: "Four cares around shea butter. The photos show the products, as they are.",
      pt: "Quatro cuidados em torno da manteiga de karité. As fotos são dos produtos, tal como são.",
    }[locale],
    valuesTitle: { fr: "Nos Valeurs", en: "Our Values", pt: "Os Nossos Valores" }[locale],
    ingredientsTitle: { fr: "Ingrédients", en: "Ingredients", pt: "Ingredientes" }[locale],
    ingredientsSubtitle: {
      fr: "Les matières végétales réellement utilisées dans nos soins disponibles.",
      en: "The plant materials actually used in our available care products.",
      pt: "As matérias vegetais realmente utilizadas nos nossos cuidados disponíveis.",
    }[locale],
    ritualTitle: { fr: "Notre Rituel", en: "Our Ritual", pt: "O Nosso Ritual" }[locale],
    ritualSubtitle: {
      fr: "De la récolte au flacon, un savoir-faire local et responsable.",
      en: "From harvest to bottle, a local and responsible craftsmanship.",
      pt: "Da colheita ao frasco, um saber-fazer local e responsável.",
    }[locale],
    faqTitle: { fr: "Questions fréquentes", en: "Frequently asked questions", pt: "Perguntas frequentes" }[locale],
    ctaFinal: {
      title: {
        fr: "Une gamme née d'Afrique de l'Ouest, pensée pour le monde.",
        en: "A range born from West Africa, crafted for the world.",
        pt: "Uma gama nascida da África Ocidental, pensada para o mundo.",
      }[locale],
      sub: {
        fr: "Distributeurs, hôtels, spas et revendeurs, contactez-nous pour découvrir nos conditions dédiées et le co-branding.",
        en: "Distributors, hotels, spas and resellers, contact us to discover our dedicated conditions and co-branding options.",
        pt: "Distribuidores, hotéis, spas e revendedores, contactem-nos para descobrir as nossas condições dedicadas e opções de co-branding.",
      }[locale],
      button: { fr: "Nous écrire", en: "Contact us", pt: "Contacte-nos" }[locale],
    },
    breadcrumb: { fr: "Cosmétiques Naturels", en: "Natural Cosmetics", pt: "Cosméticos Naturais" }[locale],
  };

  // JSON-LD Product ItemList, for Google rich snippets
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: pick({ fr: "Cosmétiques Naturels NANDA SÁ", en: "NANDA SÁ Natural Cosmetics", pt: "Cosméticos Naturais NANDA SÁ" }, locale),
    itemListElement: [...CHANTILLYS, ...SAVONS].map((p, i) => ({
      "@type": "Product",
      position: i + 1,
      name: pick(p.name, locale),
      brand: { "@type": "Brand", name: "NANDA SÁ" },
      category: pick(p.type, locale),
      image: `https://nanda-sa.com${p.image}`,
      description: pick(p.ingredients, locale),
      manufacturer: {
        "@type": "Organization",
        name: "NANDA SÁ",
        address: [
          { "@type": "PostalAddress", addressLocality: "Ziguinchor", addressCountry: "SN" },
          { "@type": "PostalAddress", addressLocality: "Canchungo", addressRegion: "Cacheu", addressCountry: "GW" },
        ],
      },
    })),
  };

  // JSON-LD FAQPage
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: pick(f.q, locale),
      acceptedAnswer: { "@type": "Answer", text: pick(f.a, locale) },
    })),
  };

  return (
    <div data-testid="page-cosmetiques" className="bg-white">
      <Seo path="/cosmetiques" />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      {/* ================= HERO ================= */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 pt-36 md:pt-48 pb-20 md:pb-28">
          <Breadcrumbs items={[{ label: t.breadcrumb, path: "/cosmetiques" }]} />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 mt-10 items-center">
            <div className="lg:col-span-7">
              <FadeUp>
                <Overline>{{ fr: "Division Cosmétiques", en: "Cosmetics Division", pt: "Divisão Cosméticos" }[locale]}</Overline>
              </FadeUp>
              <FadeUp delay={0.1}>
                <h1
                  className="font-sans font-light tracking-tight text-nanda-ink text-5xl sm:text-6xl md:text-7xl leading-[1.02] text-balance"
                  data-testid="cosmetiques-hero-title"
                >
                  {t.heroH1}
                  <br />
                  <span className="text-nanda-inkSoft">{t.heroBy}</span>
                </h1>
              </FadeUp>
              <FadeUp delay={0.2}>
                <p className="mt-10 max-w-2xl text-nanda-inkSoft text-lg md:text-xl font-light leading-relaxed">
                  {t.heroSub}
                </p>
              </FadeUp>
              <FadeUp delay={0.3} className="mt-12 flex flex-wrap items-center gap-6">
                <a
                  href="#collection"
                  className="inline-flex items-center gap-2.5 bg-nanda-terracotta text-white rounded-full px-7 py-3.5 text-sm font-medium hover:bg-[#8F5B3F] transition-colors duration-300"
                  data-testid="cosmetiques-cta-collection"
                >
                  {t.ctaDiscover}
                </a>
                <Link
                  to="/devis"
                  className="inline-flex items-center gap-2.5 text-sm font-medium text-nanda-ink link-underline"
                  data-testid="cosmetiques-cta-quote"
                >
                  {t.ctaQuote}
                  <ArrowUpRight size={15} strokeWidth={1.5} />
                </Link>
              </FadeUp>
            </div>

            <FadeUp delay={0.2} className="lg:col-span-5">
              <div className="relative overflow-hidden rounded-3xl border border-black/[0.06]">
                <img
                  src="/photos/nanda-cosmetiques/chantilly-baobab-karite.jpg"
                  alt={pick({ fr: "Chantilly Baobab Karité NANDA SÁ, pot de 100 ml", en: "NANDA SÁ Baobab & Shea Whipped Butter, 100 ml jar", pt: "Chantilly Baobá Karité NANDA SÁ, frasco de 100 ml" }, locale)}
                  className="w-full aspect-[4/5] object-cover"
                  loading="eager"
                />
                <div className="absolute bottom-4 left-4 flex items-center gap-3 bg-white/90 backdrop-blur-sm pl-4 pr-5 py-2.5 rounded-full">
                  <span className="h-1.5 w-1.5 rounded-full bg-nanda-terracotta" />
                  <span className="text-xs tracking-[0.15em] uppercase text-nanda-ink">
                    {{ fr: "Disponible", en: "Available", pt: "Disponível" }[locale]} · 100 ml
                  </span>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ================= VALUES ================= */}
      <Section className="bg-white">
        <FadeUp className="mb-14">
          <Overline>{{ fr: "Valeurs", en: "Values", pt: "Valores" }[locale]}</Overline>
          <h2 className="font-sans font-light tracking-tight text-4xl md:text-5xl leading-[1.1] text-nanda-ink text-balance">{t.valuesTitle}</h2>
        </FadeUp>
        <Stagger className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {VALUES.map((v) => (
            <StaggerItem key={v.key}>
              <div
                className="flex flex-col items-start gap-4 h-full border border-black/[0.06] rounded-3xl bg-white p-7 hover:border-black/20 hover:shadow-[0_20px_40px_rgba(0,0,0,0.04)] transition-all duration-500"
                data-testid={`value-${v.key}`}
              >
                <v.icon className="w-5 h-5 text-nanda-sage" strokeWidth={1.5} />
                <span className="font-sans font-medium tracking-tight text-lg text-nanda-ink leading-tight">{pick({ fr: v.fr, en: v.en, pt: v.pt }, locale)}</span>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </Section>

      {/* ================= CATEGORIES ================= */}
      <Section className="bg-nanda-bgAlt">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <FadeUp>
            <Overline>{{ fr: "La gamme", en: "The range", pt: "A gama" }[locale]}</Overline>
            <h2 className="font-sans font-light tracking-tight text-4xl md:text-5xl leading-[1.1] text-nanda-ink text-balance max-w-2xl">
              {{ fr: "Quatre familles de soins.", en: "Four families of care.", pt: "Quatro famílias de cuidados." }[locale]}
            </h2>
          </FadeUp>
          <FadeUp delay={0.12}>
            <p className="text-nanda-inkSoft text-base md:text-lg font-light leading-relaxed max-w-md">{t.categoriesSubtitle}</p>
          </FadeUp>
        </div>

        <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {FAMILLES.map((c, i) => (
            <StaggerItem key={c.key}>
              <a
                href={c.anchor}
                className="group block h-full border border-black/[0.06] rounded-3xl bg-white p-8 hover:border-black/20 hover:shadow-[0_20px_40px_rgba(0,0,0,0.04)] transition-all duration-500"
                data-testid={`famille-${c.key}`}
              >
                <div className="label-overline text-nanda-muted">0{i + 1}</div>
                <h3 className="font-sans font-medium tracking-tight text-xl md:text-2xl text-nanda-ink leading-tight mt-5">{pick(c.label, locale)}</h3>
                <p className="mt-4 text-sm text-nanda-inkSoft font-light leading-relaxed">{pick(c.desc, locale)}</p>
                <div className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-nanda-ink group-hover:translate-x-1 transition-transform duration-500">
                  {{ fr: "Voir", en: "View", pt: "Ver" }[locale]}
                  <ArrowUpRight size={14} strokeWidth={1.5} />
                </div>
              </a>
            </StaggerItem>
          ))}
        </Stagger>
      </Section>

      {/* ================= CHANTILLY & SOINS FOUETTÉS ================= */}
      <Section id="collection" className="bg-white scroll-anchor">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <FadeUp>
            <Overline tone="text-nanda-terracotta">{{ fr: "Disponible", en: "Available", pt: "Disponível" }[locale]}</Overline>
            <h2 className="font-sans font-light tracking-tight text-4xl md:text-5xl leading-[1.1] text-nanda-ink text-balance" data-testid="chantillys-title">{t.productsTitle}</h2>
          </FadeUp>
          <FadeUp delay={0.12}>
            <p className="text-nanda-inkSoft text-base md:text-lg font-light leading-relaxed max-w-md">{t.productsSubtitle}</p>
          </FadeUp>
        </div>

        <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {CHANTILLYS.map((p) => (
            <StaggerItem key={p.slug}>
              <div className="group flex flex-col h-full" data-testid={`product-${p.slug}`}>
                <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-black/[0.06] bg-white">
                  <img
                    src={p.image}
                    alt={`${pick(p.name, locale)} NANDA SÁ${p.volume ? " " + p.volume : ""}`}
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-[1200ms] ease-out"
                    loading="lazy"
                  />
                </div>
                <div className="mt-7 flex flex-col gap-2.5 flex-1">
                  <div className="label-overline text-nanda-muted">{pick(p.type, locale)}</div>
                  <h3 className="font-sans font-medium tracking-tight text-xl md:text-2xl text-nanda-ink leading-tight">{pick(p.name, locale)}</h3>
                  <div className="text-sm text-nanda-inkSoft font-light">{pick(p.ingredients, locale)}</div>
                  <p className="text-sm text-nanda-muted font-light leading-relaxed">{pick(p.usage, locale)}</p>
                  {p.volume && <div className="text-xs tracking-[0.2em] uppercase text-nanda-muted">{p.volume}</div>}
                  <Link
                    to="/contact?subject=Cosmetiques"
                    className="mt-3 inline-flex items-center gap-2.5 text-sm font-medium text-nanda-ink link-underline self-start"
                    data-testid={`order-${p.slug}`}
                  >
                    {{ fr: "Commander", en: "Order", pt: "Encomendar" }[locale]}
                    <ArrowUpRight size={15} strokeWidth={1.5} />
                  </Link>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </Section>

      {/* ================= HUILES COSMÉTIQUES ================= */}
      <Section id="huiles" className="bg-nanda-bgAlt scroll-anchor">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20 items-center">
          <FadeUp>
            <div className="overflow-hidden rounded-3xl">
              <img
                src="/photos/nanda-cosmetiques/ing-baobab-hero.jpg"
                alt={pick({ fr: "Graines de baobab, matière des huiles végétales NANDA SÁ", en: "Baobab seeds, the source of NANDA SÁ plant oils", pt: "Sementes de baobá, matéria dos óleos vegetais NANDA SÁ" }, locale)}
                className="w-full aspect-[4/3] object-cover"
                loading="lazy"
              />
            </div>
          </FadeUp>
          <FadeUp delay={0.12}>
            <Overline>{{ fr: "Huiles cosmétiques", en: "Cosmetic oils", pt: "Óleos cosméticos" }[locale]}</Overline>
            <h2 className="font-sans font-light tracking-tight text-4xl md:text-5xl leading-[1.1] text-nanda-ink text-balance" data-testid="huiles-title">
              {{ fr: "Les huiles végétales de la Maison.", en: "The House's plant oils.", pt: "Os óleos vegetais da Casa." }[locale]}
            </h2>
            <p className="mt-7 max-w-lg text-nanda-inkSoft text-base md:text-lg font-light leading-relaxed">
              {{ fr: "Huiles végétales pressées à froid : baobab, hibiscus et moringa. Formats et disponibilité sur demande.", en: "Cold-pressed plant oils: baobab, hibiscus and moringa. Formats and availability on request.", pt: "Óleos vegetais prensados a frio: baobá, hibisco e moringa. Formatos e disponibilidade a pedido." }[locale]}
            </p>
            <div className="mt-8 label-overline text-nanda-ink" data-testid="huiles-list">
              Baobab · Hibiscus · Moringa
            </div>
            <Link
              to="/contact?subject=Cosmetiques"
              className="mt-10 inline-flex items-center gap-2.5 text-sm font-medium text-nanda-ink link-underline"
              data-testid="huiles-cta-contact"
            >
              {{ fr: "Demander les disponibilités", en: "Ask for availability", pt: "Pedir disponibilidade" }[locale]}
              <ArrowUpRight size={15} strokeWidth={1.5} />
            </Link>
          </FadeUp>
        </div>
      </Section>

      {/* ================= BAUMES & SAVONS ================= */}
      <Section className="bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          <FadeUp id="baumes" className="scroll-anchor">
            <Overline>{{ fr: "Baumes", en: "Balms", pt: "Bálsamos" }[locale]}</Overline>
            <h2 className="font-sans font-light tracking-tight text-4xl md:text-5xl leading-[1.1] text-nanda-ink text-balance" data-testid="baumes-title">
              {{ fr: "En cours de développement.", en: "Currently in development.", pt: "Em desenvolvimento." }[locale]}
            </h2>
            <p className="mt-7 max-w-md text-nanda-inkSoft text-base md:text-lg font-light leading-relaxed">
              {{ fr: "Nos baumes sont en cours de formulation. Ils seront présentés ici lorsqu'ils seront finalisés.", en: "Our balms are currently being formulated. They will be presented here once finalised.", pt: "Os nossos bálsamos estão em formulação. Serão apresentados aqui quando finalizados." }[locale]}
            </p>
          </FadeUp>
          <FadeUp delay={0.12} id="savons" className="scroll-anchor">
            <Overline>{{ fr: "Savons & soins", en: "Soaps & care", pt: "Sabonetes & cuidados" }[locale]}</Overline>
            {SAVONS.map((p) => (
              <div key={p.slug} className="group flex flex-col sm:flex-row gap-8 items-start" data-testid={`product-${p.slug}`}>
                <div className="relative w-full sm:w-56 aspect-square rounded-3xl overflow-hidden border border-black/[0.06] bg-white shrink-0">
                  <img
                    src={p.image}
                    alt={`${pick(p.name, locale)} NANDA SÁ`}
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-[1200ms] ease-out"
                    loading="lazy"
                  />
                </div>
                <div className="flex flex-col gap-2.5">
                  <div className="label-overline text-nanda-muted">{pick(p.type, locale)}</div>
                  <h3 className="font-sans font-medium tracking-tight text-xl md:text-2xl text-nanda-ink leading-tight">{pick(p.name, locale)}</h3>
                  <div className="text-sm text-nanda-inkSoft font-light">{pick(p.ingredients, locale)}</div>
                  <p className="text-sm text-nanda-muted font-light leading-relaxed">{pick(p.usage, locale)}</p>
                  <Link
                    to="/contact?subject=Cosmetiques"
                    className="mt-3 inline-flex items-center gap-2.5 text-sm font-medium text-nanda-ink link-underline self-start"
                    data-testid={`order-${p.slug}`}
                  >
                    {{ fr: "Commander", en: "Order", pt: "Encomendar" }[locale]}
                    <ArrowUpRight size={15} strokeWidth={1.5} />
                  </Link>
                </div>
              </div>
            ))}
          </FadeUp>
        </div>
      </Section>

      {/* ================= INGREDIENTS ================= */}
      <Section className="bg-nanda-bgAlt">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <FadeUp className="lg:col-span-4">
            <Overline>{{ fr: "Ingrédients", en: "Ingredients", pt: "Ingredientes" }[locale]}</Overline>
            <h2 className="font-sans font-light tracking-tight text-4xl md:text-5xl leading-[1.1] text-nanda-ink text-balance">{t.ingredientsTitle}</h2>
            <p className="mt-7 text-nanda-inkSoft text-base font-light leading-relaxed max-w-sm">{t.ingredientsSubtitle}</p>
          </FadeUp>
          <div className="lg:col-span-8">
            <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
              {INGREDIENTS.map((ing) => {
                const slugMap = { "Karité": "karite", "Baobab": "baobab", "Hibiscus": "hibiscus" };
                const slug = slugMap[ing.name];
                const content = (
                  <>
                    <div className="flex items-baseline justify-between gap-4">
                      <h3 className="font-sans font-medium tracking-tight text-xl md:text-2xl text-nanda-ink leading-tight">
                        {ing.name}
                        {slug && (
                          <ArrowUpRight className="inline w-4 h-4 ml-2 text-nanda-terracotta opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        )}
                      </h3>
                      <span className="text-xs italic text-nanda-muted font-light">{ing.latin}</span>
                    </div>
                    <p className="mt-3 text-sm text-nanda-inkSoft font-light leading-relaxed">{pick({ fr: ing.fr, en: ing.en, pt: ing.pt }, locale)}</p>
                    {slug && (
                      <span className="mt-4 inline-block label-overline text-nanda-terracotta">
                        {{ fr: "Lire la fiche", en: "Read the sheet", pt: "Ler a ficha" }[locale]}
                      </span>
                    )}
                  </>
                );
                if (slug) {
                  return (
                    <StaggerItem key={ing.name}>
                      <Link
                        to={`/cosmetiques/ingredients/${slug}`}
                        className="group block border-t border-nanda-line pt-7"
                        data-testid={`ingredient-${ing.name.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {content}
                      </Link>
                    </StaggerItem>
                  );
                }
                return (
                  <StaggerItem key={ing.name}>
                    <div className="border-t border-nanda-line pt-7" data-testid={`ingredient-${ing.name.toLowerCase().replace(/\s+/g, "-")}`}>
                      {content}
                    </div>
                  </StaggerItem>
                );
              })}
            </Stagger>
          </div>
        </div>
      </Section>

      {/* ================= RITUAL / PROCESS ================= */}
      <Section className="bg-white">
        <FadeUp className="max-w-3xl">
          <Overline>{{ fr: "Savoir-faire", en: "Know-how", pt: "Saber-fazer" }[locale]}</Overline>
          <h2 className="font-sans font-light tracking-tight text-4xl md:text-5xl leading-[1.1] text-nanda-ink text-balance">{t.ritualTitle}</h2>
          <p className="mt-7 text-nanda-inkSoft text-base md:text-lg font-light leading-relaxed">{t.ritualSubtitle}</p>
        </FadeUp>

        <Stagger className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {RITUAL.map((r) => (
            <StaggerItem key={r.step}>
              <div className="flex flex-col gap-4 border-t border-nanda-line pt-7" data-testid={`ritual-step-${r.step}`}>
                <div className="label-overline text-nanda-terracotta">{r.step}</div>
                <h3 className="font-sans font-medium tracking-tight text-xl text-nanda-ink leading-tight">{pick({ fr: r.fr, en: r.en, pt: r.pt }, locale)}</h3>
                <p className="text-sm text-nanda-inkSoft font-light leading-relaxed">{pick({ fr: r.descFr, en: r.descEn, pt: r.descPt }, locale)}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </Section>

      {/* ================= FAQ ================= */}
      <Section className="bg-nanda-bgAlt">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <FadeUp className="lg:col-span-4">
            <Overline>FAQ</Overline>
            <h2 className="font-sans font-light tracking-tight text-4xl md:text-5xl leading-[1.1] text-nanda-ink text-balance">{t.faqTitle}</h2>
          </FadeUp>
          <div className="lg:col-span-8">
            <div className="divide-y divide-nanda-line border-t border-b border-nanda-line">
              {FAQ.map((f, i) => (
                <div key={i} data-testid={`faq-item-${i}`}>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full text-left py-6 flex items-center justify-between gap-6 hover:text-nanda-terracotta transition-colors duration-300"
                    data-testid={`faq-toggle-${i}`}
                  >
                    <span className="font-sans font-medium tracking-tight text-lg text-nanda-ink">{pick(f.q, locale)}</span>
                    <ChevronDown className={`w-5 h-5 flex-shrink-0 text-nanda-muted transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`} strokeWidth={1.5} />
                  </button>
                  {openFaq === i && (
                    <p className="pb-7 text-nanda-inkSoft font-light leading-relaxed max-w-2xl" data-testid={`faq-answer-${i}`}>
                      {pick(f.a, locale)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* ================= DOWNLOADS ================= */}
      <Section className="bg-white" id="telechargements">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <FadeUp>
            <Overline>{{ fr: "Téléchargements", en: "Downloads", pt: "Descargas" }[locale]}</Overline>
            <h2 className="font-sans font-light tracking-tight text-4xl md:text-5xl leading-[1.1] text-nanda-ink text-balance">
              {{
                fr: "Nos livrables",
                en: "Our brochures",
                pt: "Os nossos livretes",
              }[locale]}
            </h2>
          </FadeUp>
          <FadeUp delay={0.12}>
            <p className="text-nanda-inkSoft text-base md:text-lg font-light leading-relaxed max-w-md">
              {{
                fr: "Deux documents PDF trilingues (EN·PT·FR), prêts à consulter ou à imprimer.",
                en: "Two trilingual PDF documents (EN·PT·FR), ready to view or print.",
                pt: "Dois documentos PDF trilingues (EN·PT·FR), prontos a consultar ou imprimir.",
              }[locale]}
            </p>
          </FadeUp>
        </div>

        <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <StaggerItem>
            <a
              href="/pdf/nanda-cosmetics-catalogue-2026.pdf"
              target="_blank"
              rel="noreferrer"
              className="group flex flex-col h-full border border-black/[0.06] rounded-3xl bg-white p-8 md:p-10 hover:border-black/20 hover:shadow-[0_20px_40px_rgba(0,0,0,0.04)] transition-all duration-500"
              data-testid="download-catalogue-brand-book"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="label-overline text-nanda-gold mb-4">Brand Book · Volume I</div>
                  <h3 className="font-sans font-light tracking-tight text-2xl md:text-3xl text-nanda-ink leading-tight text-balance">
                    {{
                      fr: "Catalogue Cosmétique 2026",
                      en: "2026 Cosmetics Catalogue",
                      pt: "Catálogo Cosmético 2026",
                    }[locale]}
                  </h3>
                  <p className="mt-4 text-sm text-nanda-inkSoft font-light leading-relaxed">
                    {{
                      fr: "26 pages, éditorial, style Aesop / Le Labo. Manifeste, six produits, bibliothèque botanique.",
                      en: "26 pages, editorial, Aesop / Le Labo style. Manifesto, six products, botanical library.",
                      pt: "26 páginas, editorial, estilo Aesop / Le Labo. Manifesto, seis produtos, biblioteca botânica.",
                    }[locale]}
                  </p>
                </div>
                <ArrowUpRight size={20} strokeWidth={1.5} className="text-nanda-terracotta flex-shrink-0 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-500" />
              </div>
              <div className="mt-auto pt-8">
                <div className="flex items-center justify-between text-xs uppercase tracking-wider text-nanda-muted border-t border-nanda-line pt-5">
                  <span>PDF · 12 MB · 26 {{ fr: "pages", en: "pages", pt: "páginas" }[locale]}</span>
                  <span className="text-nanda-terracotta">EN · PT · FR</span>
                </div>
              </div>
            </a>
          </StaggerItem>

          <StaggerItem>
            <a
              href="/pdf/nanda-cosmetics-b2b-distribution-2026.pdf"
              target="_blank"
              rel="noreferrer"
              className="group flex flex-col h-full border border-black/[0.06] rounded-3xl bg-white p-8 md:p-10 hover:border-black/20 hover:shadow-[0_20px_40px_rgba(0,0,0,0.04)] transition-all duration-500"
              data-testid="download-catalogue-b2b"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="label-overline text-nanda-terracotta mb-4">
                    {{ fr: "Distribution · B2B", en: "Distribution · B2B", pt: "Distribuição · B2B" }[locale]}
                  </div>
                  <h3 className="font-sans font-light tracking-tight text-2xl md:text-3xl text-nanda-ink leading-tight text-balance">
                    {{
                      fr: "Livret Distribution 2026",
                      en: "2026 Distribution Booklet",
                      pt: "Livrete Distribuição 2026",
                    }[locale]}
                  </h3>
                  <p className="mt-4 text-sm text-nanda-inkSoft font-light leading-relaxed">
                    {{
                      fr: "12 pages pour hôtels, spas, retailers et distributeurs. Programmes, conditions commerciales, onboarding.",
                      en: "12 pages for hotels, spas, retailers and distributors. Programmes, commercial conditions, onboarding.",
                      pt: "12 páginas para hotéis, spas, retalho e distribuidores. Programas, condições comerciais, onboarding.",
                    }[locale]}
                  </p>
                </div>
                <ArrowUpRight size={20} strokeWidth={1.5} className="text-nanda-terracotta flex-shrink-0 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-500" />
              </div>
              <div className="mt-auto pt-8">
                <div className="flex items-center justify-between text-xs uppercase tracking-wider text-nanda-muted border-t border-nanda-line pt-5">
                  <span>PDF · 5.3 MB · 12 {{ fr: "pages", en: "pages", pt: "páginas" }[locale]}</span>
                  <span className="text-nanda-terracotta">EN · PT · FR</span>
                </div>
              </div>
            </a>
          </StaggerItem>
        </Stagger>

        <FadeUp delay={0.15}>
          <a
            href="/pdf/nanda-cosmetics-catalogue-v6.zip"
            className="mt-10 inline-flex items-center gap-2.5 text-sm font-medium text-nanda-ink link-underline"
            data-testid="download-zip-full"
          >
            <Download size={15} strokeWidth={1.5} />
            {{
              fr: "Télécharger le pack complet (ZIP · 41 MB)",
              en: "Download the full pack (ZIP · 41 MB)",
              pt: "Descarregar o pacote completo (ZIP · 41 MB)",
            }[locale]}
          </a>
        </FadeUp>
      </Section>

      {/* ================= FINAL CTA ================= */}
      <Section className="bg-nanda-bgAlt text-center">
        <div className="max-w-3xl mx-auto">
          <FadeUp>
            <h2 className="font-sans font-light tracking-tight text-4xl md:text-5xl leading-[1.1] text-nanda-ink text-balance">{t.ctaFinal.title}</h2>
            <p className="mt-7 text-nanda-inkSoft text-base md:text-lg font-light leading-relaxed">{t.ctaFinal.sub}</p>
          </FadeUp>
          <FadeUp delay={0.15} className="mt-12 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2.5 bg-nanda-terracotta text-white rounded-full px-7 py-3.5 text-sm font-medium hover:bg-[#8F5B3F] transition-colors duration-300"
              data-testid="cosmetiques-cta-final"
            >
              {t.ctaFinal.button}
              <ArrowUpRight size={15} strokeWidth={1.5} />
            </Link>
            <Link
              to="/cosmetiques/amenity-kit"
              className="inline-flex items-center gap-2.5 border border-nanda-ink/15 bg-white text-nanda-ink rounded-full px-6 py-3.5 text-sm font-medium hover:border-nanda-ink transition-colors duration-300"
              data-testid="cosmetiques-cta-amenity"
            >
              {{ fr: "Configurer un Kit Amenity", en: "Configure an Amenity Kit", pt: "Configurar um Kit Amenity" }[locale]}
            </Link>
            <Link
              to="/carnet"
              className="inline-flex items-center gap-2.5 border border-nanda-ink/15 bg-white text-nanda-ink rounded-full px-6 py-3.5 text-sm font-medium hover:border-nanda-ink transition-colors duration-300"
              data-testid="cosmetiques-cta-carnet"
            >
              {{ fr: "Lire le Carnet des ingrédients", en: "Read the ingredient Journal", pt: "Ler o Caderno de ingredientes" }[locale]}
            </Link>
          </FadeUp>
        </div>
      </Section>
    </div>
  );
}
